import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import { FeedbackCampaign } from 'app/services/api/common/feedback.service'

interface FeedbackSurveyDialogProps {
  campaign: FeedbackCampaign | null
  open: boolean
  isActing: boolean
  onTakeSurvey: () => void
  onAlreadyTaken: () => void
  onMaybeLater: () => void
}

export function FeedbackSurveyDialog({
  campaign,
  open,
  isActing,
  onTakeSurvey,
  onAlreadyTaken,
  onMaybeLater,
}: FeedbackSurveyDialogProps) {
  if (!campaign) {
    return null
  }

  const parsedEndDate = new Date(campaign.ends_at)
  const endDate = Number.isNaN(parsedEndDate.getTime())
    ? null
    : new Intl.DateTimeFormat(undefined, {
        day: 'numeric',
        month: 'long',
      }).format(parsedEndDate)

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          onMaybeLater()
        }
      }}
      aria-labelledby='feedback-survey-title'
      PaperProps={{
        sx: {
          width: 'min(520px, calc(100% - 32px))',
          maxWidth: 'none',
          borderRadius: '14px',
          borderTop: '6px solid #0000A0',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 2, sm: 2.75 }, pb: { xs: 2, sm: 2.5 } }}>
        <IconButton
          aria-label='Close feedback survey'
          onClick={onMaybeLater}
          disabled={isActing}
          sx={{ position: 'absolute', right: 16, top: 16, color: '#8B8984' }}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ pr: 5 }}>
          <Typography
            id='feedback-survey-title'
            component='h2'
            sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 500 }}
          >
            Help us improve the Upload Hub
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              color: '#5D5B58',
              fontSize: '0.95rem',
              lineHeight: 1.45,
            }}
          >
            You&apos;re one of our most active users, so your feedback matters
            more than most.
          </Typography>
          <Typography
            sx={{
              mt: 1,
              color: '#5D5B58',
              fontSize: '0.95rem',
              lineHeight: 1.45,
            }}
          >
            Anonymous, under one minute
            {endDate ? `, open until ${endDate}` : ''}.
          </Typography>
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ mt: 2.25, alignItems: { xs: 'stretch', sm: 'center' } }}
        >
          <Button
            variant='contained'
            onClick={onTakeSurvey}
            disabled={isActing}
            sx={{
              minHeight: 40,
              px: 2,
              backgroundColor: '#0000A0',
              fontSize: '0.9rem',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#000070' },
            }}
          >
            Take the survey
          </Button>
          <Button
            variant='outlined'
            onClick={onAlreadyTaken}
            disabled={isActing}
            sx={{
              minHeight: 40,
              px: 2,
              color: '#202020',
              borderColor: '#CFCFCF',
              fontSize: '0.9rem',
            }}
          >
            Already taken
          </Button>
          <Button
            variant='text'
            onClick={onMaybeLater}
            disabled={isActing}
            sx={{ minHeight: 36, color: '#5D5B58', fontSize: '0.85rem' }}
          >
            Maybe later
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
